import React, { useState, useEffect } from 'react';
import { notificationAPI } from '../services/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Settings, Bell, Mail, FileText, CalendarCheck, CalendarX, DoorOpen, UserCheck, Megaphone, CreditCard, Loader2, Car, FileBarChart } from 'lucide-react';

const NOTIFICATION_TYPES = [
  { key: 'invoice_created', label: 'New Invoice', description: 'When a new invoice is generated for your villa', icon: FileText, color: 'text-amber-500' },
  { key: 'booking_approved', label: 'Booking Approved', description: 'When your facility booking is approved', icon: CalendarCheck, color: 'text-emerald-500' },
  { key: 'booking_rejected', label: 'Booking Rejected', description: 'When your facility booking is rejected', icon: CalendarX, color: 'text-red-500' },
  { key: 'gate_pass_used', label: 'Gate Pass Used', description: 'When someone enters using your gate pass', icon: DoorOpen, color: 'text-gk-teal' },
  { key: 'staff_checkin', label: 'Staff Arrival', description: 'When staff checks in at your villa', icon: UserCheck, color: 'text-gk-navy-light' },
  { key: 'vehicle_checkin', label: 'Vehicle Check-In', description: 'When your registered vehicle is detected at the gate', icon: Car, color: 'text-indigo-500' },
  { key: 'notice_posted', label: 'Community Notices', description: 'When management posts a new notice', icon: Megaphone, color: 'text-blue-500' },
  { key: 'payment_confirmed', label: 'Payment Confirmed', description: 'When your payment is confirmed with receipt', icon: CreditCard, color: 'text-emerald-500' },
];

const EMAIL_ONLY_TYPES = [
  { key: 'monthly_recap_email', label: 'Monthly Recap Email', description: 'A personal summary of your gate passes, deliveries, visitors and bookings every month', icon: FileBarChart, color: 'text-gk-teal' },
];

export function NotificationPreferences() {
  const [isOpen, setIsOpen] = useState(false);
  const [preferences, setPreferences] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) fetchPreferences();
  }, [isOpen]);

  const fetchPreferences = async () => {
    setLoading(true);
    try {
      const res = await notificationAPI.getPreferences();
      setPreferences(res.data.preferences || {});
    } catch (e) {
      toast.error('Failed to load preferences');
    } finally {
      setLoading(false);
    }
  };

  const togglePref = (type, channel) => {
    setPreferences(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [channel]: !(prev[type]?.[channel] ?? true)
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await notificationAPI.updatePreferences(preferences);
      toast.success('Notification preferences saved');
      setIsOpen(false);
    } catch (e) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        data-testid="notification-preferences-button"
        onClick={() => setIsOpen(true)}
        className="p-2 rounded-lg hover:bg-white/10 transition-colors"
        title="Notification Preferences"
      >
        <Settings className="w-5 h-5 text-white" />
      </button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Settings className="w-5 h-5 text-gk-teal" />
              Notification Preferences
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">Choose how you want to be notified for each event</p>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-gk-teal" />
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto -mx-6 px-6">
              <div className="flex items-center justify-end gap-8 mb-3 pr-1 sticky top-0 bg-white pt-2 pb-2 z-10">
                <div className="flex items-center gap-1 text-xs font-medium text-slate-500">
                  <Bell className="w-3.5 h-3.5" />
                  In-App
                </div>
                <div className="flex items-center gap-1 text-xs font-medium text-slate-500">
                  <Mail className="w-3.5 h-3.5" />
                  Email
                </div>
              </div>

              <div className="space-y-1">
                {NOTIFICATION_TYPES.map(({ key, label, description, icon: Icon, color }) => {
                  const inApp = preferences[key]?.in_app ?? true;
                  const email = preferences[key]?.email ?? true;

                  return (
                    <div
                      key={key}
                      data-testid={`pref-row-${key}`}
                      className="flex items-center gap-3 py-3 px-3 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      <div className={`flex-shrink-0 w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center`}>
                        <Icon className={`w-4.5 h-4.5 ${color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900">{label}</p>
                        <p className="text-xs text-slate-500 leading-snug">{description}</p>
                      </div>
                      <div className="flex items-center gap-6 flex-shrink-0">
                        <button
                          data-testid={`toggle-inapp-${key}`}
                          onClick={() => togglePref(key, 'in_app')}
                          className={`relative w-10 h-5.5 rounded-full transition-colors duration-200 ${
                            inApp ? 'bg-gk-teal' : 'bg-slate-300'
                          }`}
                          style={{ width: '40px', height: '22px' }}
                        >
                          <span
                            className={`absolute top-0.5 left-0.5 w-4.5 h-4.5 bg-white rounded-full shadow transition-transform duration-200 ${
                              inApp ? 'translate-x-[18px]' : 'translate-x-0'
                            }`}
                            style={{ width: '18px', height: '18px' }}
                          />
                        </button>
                        <button
                          data-testid={`toggle-email-${key}`}
                          onClick={() => togglePref(key, 'email')}
                          className={`relative rounded-full transition-colors duration-200 ${
                            email ? 'bg-gk-teal' : 'bg-slate-300'
                          }`}
                          style={{ width: '40px', height: '22px' }}
                        >
                          <span
                            className={`absolute top-0.5 left-0.5 bg-white rounded-full shadow transition-transform duration-200 ${
                              email ? 'translate-x-[18px]' : 'translate-x-0'
                            }`}
                            style={{ width: '18px', height: '18px' }}
                          />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 pt-3 border-t">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 px-3">Email Digests</p>
                {EMAIL_ONLY_TYPES.map(({ key, label, description, icon: Icon, color }) => {
                  const email = preferences[key]?.email ?? true;
                  return (
                    <div
                      key={key}
                      data-testid={`pref-row-${key}`}
                      className="flex items-center gap-3 py-3 px-3 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center">
                        <Icon className={`w-4.5 h-4.5 ${color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900">{label}</p>
                        <p className="text-xs text-slate-500 leading-snug">{description}</p>
                      </div>
                      <div className="flex items-center gap-6 flex-shrink-0">
                        {/* spacer to align with two-toggle rows */}
                        <span style={{ width: '40px' }} />
                        <button
                          data-testid={`toggle-email-${key}`}
                          onClick={() => togglePref(key, 'email')}
                          className={`relative rounded-full transition-colors duration-200 ${
                            email ? 'bg-gk-teal' : 'bg-slate-300'
                          }`}
                          style={{ width: '40px', height: '22px' }}
                        >
                          <span
                            className={`absolute top-0.5 left-0.5 bg-white rounded-full shadow transition-transform duration-200 ${
                              email ? 'translate-x-[18px]' : 'translate-x-0'
                            }`}
                            style={{ width: '18px', height: '18px' }}
                          />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-4 border-t mt-4">
            <Button
              data-testid="save-preferences-button"
              onClick={handleSave}
              disabled={saving}
              className="flex-1 bg-gk-teal hover:bg-gk-teal-hover"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Preferences
            </Button>
            <Button onClick={() => setIsOpen(false)} variant="outline">
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
